import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Gift, Wallet, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/site/Seo";
import { Squiggle, Star, Swoosh } from "@/components/site/Doodles";
import { useCustomer } from "@/context/CustomerContext";
import { api, API, errMsg } from "@/lib/api";
import { DURATION, STYLE, FORMAT, egp, inputCls, cardCls, btnPrimary, btnGhost, orderLabel } from "@/lib/labels";

const BrandBox = ({ c }) => {
  const { setCustomer } = useCustomer();
  const ref = useRef(null);
  const [phone, setPhone] = useState(c.phone);
  const [busy, setBusy] = useState(false);
  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("logo", file);
    setBusy(true);
    try { setCustomer((await api.post("/customer/profile/logo", fd)).data); toast.success("اللوجو اتحدث في حسابك"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const savePhone = async () => {
    try { setCustomer((await api.put("/customer/phone", { phone: phone.trim() })).data); toast.success("رقم الموبايل اتحفظ"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const img = c.logo && c.logo.content_type.startsWith("image/") && c.logo.content_type !== "image/svg+xml";
  return (
    <div className="flex items-center gap-4 rounded-3xl bg-black/30 p-4" data-testid="order-profile-summary">
      <button type="button" onClick={() => ref.current?.click()} aria-label="غيّر اللوجو" data-testid="order-logo-change-btn"
        className="group relative grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white">
        {img ? <img src={`${API}/customer/profile/logo?v=${encodeURIComponent(c.logo.uploaded_at)}`} alt="لوجو البراند" className="h-full w-full object-contain p-2" data-testid="order-logo-img" />
          : <span className="px-1 text-center text-[10px] text-[var(--km-purple)]">{c.logo ? c.logo.filename : "ارفع لوجو"}</span>}
        <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[10px] font-bold text-white sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">{busy ? "بيترفع..." : "غيّر اللوجو"}</span>
      </button>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp,.gif,.svg,.pdf" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="order-logo-input" />
      <span className="min-w-0 flex-1 space-y-1 text-sm">
        <span className="font-head block text-lg font-bold text-white" data-testid="order-brand-name">{c.brand_name}</span>
        <span className="flex items-center gap-2">
          <input type="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} aria-label="رقم الموبايل"
            className="w-full min-w-0 rounded-xl border border-purple-500/30 bg-black/40 px-2 py-1 text-right text-white outline-none focus:border-[var(--km-pink)]" data-testid="order-phone-input" />
          {phone.trim() !== c.phone && <button type="button" onClick={savePhone} className="shrink-0 rounded-full bg-[var(--km-pink)] px-3 py-1 text-xs font-bold text-white" data-testid="order-phone-save">حفظ</button>}
        </span>
        <span className="block truncate text-purple-200/80" dir="ltr" style={{ textAlign: "right" }} data-testid="order-email">{c.email}</span>
      </span>
    </div>
  );
};

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const Choice = ({ active, onClick, title, sub, testId, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`rounded-2xl border-2 p-4 text-right transition-colors disabled:opacity-40 ${active ? "border-[var(--km-pink)] bg-[var(--km-pink)]/15" : "border-purple-500/30 bg-black/30 hover:border-purple-300/60"}`}>
    <span className="font-head block font-bold text-white">{title}</span>
    {sub && <span className="block text-sm text-purple-200/80">{sub}</span>}
  </button>
);

const ThankYou = ({ order }) => (
  <motion.div initial={{ opacity: 0, scale: 0.9, rotate: -2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160 }}
    className="relative overflow-hidden rounded-[2.5rem] border-2 border-[var(--km-cyan)] bg-[var(--km-bg2)] p-8 text-center sm:p-14" data-testid="create-ad-thank-you">
    <Star className="km-float absolute left-8 top-8 h-10 w-10" />
    <CheckCircle2 className="mx-auto mb-6 h-20 w-20 text-[var(--km-cyan)]" />
    <h2 className="font-display text-4xl leading-snug text-white sm:text-5xl" data-testid="thank-you-message">
      شكراً ليك! طلب إعلانك وصل لخرابيش موشن بنجاح، تابع حالة أوردرَك من داش بورد حسابك.
    </h2>
    <p className="mt-4 text-purple-200/85" data-testid="thank-you-order-label">{orderLabel(order)} · {order.is_free ? "مجاني" : `اتخصم ${egp(order.price)} من محفظتك`}</p>
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      <Link to={`/account/orders/${order.id}`} className={btnPrimary} data-testid="thank-you-track-btn">تابع الأوردر</Link>
    </div>
  </motion.div>
);

const Gate = () => (
  <div className={`${cardCls} p-8 text-center sm:p-12`} data-testid="create-ad-login-gate">
    <h2 className="font-display text-3xl text-white sm:text-4xl">سجل دخول عشان تطلب إعلانك</h2>
    <p className="mt-3 text-purple-200/85">بحساب واحد بياناتك ولوجو البراند بيتملوا لوحدهم، وتتابع أوردراتك ومحفظتك.</p>
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      <Link to="/login?next=/create-ad" className={btnPrimary} data-testid="gate-login-btn"><LogIn className="h-4 w-4" /> سجل دخول</Link>
      <Link to="/register?next=/create-ad" className={btnGhost} data-testid="gate-register-btn"><UserPlus className="h-4 w-4" /> اعمل حساب جديد</Link>
    </div>
  </div>
);

const ProfileSummary = ({ c }) => (
  <div className="flex items-center gap-4 rounded-3xl bg-black/30 p-4" data-testid="order-profile-summary">
    <span className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white">
      {c.logo && c.logo.content_type.startsWith("image/") && c.logo.content_type !== "image/svg+xml"
        ? <img src={`${API}/customer/profile/logo?v=${encodeURIComponent(c.logo.uploaded_at)}`} alt="لوجو البراند" className="h-full w-full object-contain p-2" />
        : <span className="px-1 text-center text-[10px] text-[var(--km-purple)]">{c.logo ? c.logo.filename : "مفيش لوجو"}</span>}
    </span>
    <span className="min-w-0 flex-1 text-sm">
      <span className="font-head block text-lg font-bold text-white" data-testid="order-brand-name">{c.brand_name}</span>
      <span className="block text-purple-200/80" dir="ltr" style={{ textAlign: "right" }}>{c.phone}</span>
      <span className="block truncate text-purple-200/80" dir="ltr" style={{ textAlign: "right" }}>{c.email}</span>
    </span>
  </div>
);

const OrderForm = ({ dash, onDone }) => {
  const { customer } = useCustomer();
  const c = customer || dash.customer;
  const [duration, setDuration] = useState("short");
  const [style, setStyle] = useState("direct");
  const [format, setFormat] = useState("landscape");
  const [useReward, setUseReward] = useState(false);
  const [keyPoints, setKeyPoints] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const available = dash.loyalty.available;

  const price = useReward ? 0 : dash.pricing[duration];
  const enough = c.wallet_balance >= price;
  const pickDuration = (d) => { setDuration(d); if (d === "short") { setStyle("direct"); setUseReward(false); } };
  const toggleReward = () => { const v = !useReward; setUseReward(v); if (v) setDuration("long"); };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (keyPoints.trim().length < 3) return toast.error("قولنا أهم نقط عايزها في الإعلان");
    setBusy(true);
    const fd = new FormData();
    Object.entries({ duration, style: duration === "short" ? "direct" : style, format, key_points: keyPoints.trim(), notes: notes.trim(),
      use_reward: useReward, idempotency_key: key.current }).forEach(([k, v]) => fd.append(k, v));
    try {
      const { data } = await api.post("/customer/orders", fd);
      key.current = newKey();
      onDone(data.order);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-6 rounded-[2.5rem] border border-purple-500/25 bg-[var(--km-bg2)]/90 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.5)] sm:p-10" data-testid="create-ad-form">
      <BrandBox c={c} />
      {available > 0 && (
        <button type="button" onClick={toggleReward} data-testid="use-reward-toggle"
          className={`flex w-full items-center gap-3 rounded-2xl border-2 p-4 text-right ${useReward ? "border-[var(--km-cyan)] bg-[var(--km-cyan)]/15" : "border-dashed border-[var(--km-cyan)]/50"}`}>
          <Gift className="h-6 w-6 text-[var(--km-cyan)]" />
          <span className="flex-1"><span className="font-head block font-bold text-white">استخدم إعلانك المجاني ({available} متاح)</span>
            <span className="block text-sm text-purple-200/80">إعلان من 30 لـ 60 ثانية بـ 0 جنيه، ومش هيتخصم من محفظتك</span></span>
          <span className={`h-6 w-6 rounded-full border-2 ${useReward ? "border-[var(--km-cyan)] bg-[var(--km-cyan)]" : "border-purple-400"}`} />
        </button>
      )}
      <div>
        <p className="font-head mb-2 font-bold text-white">مدة الإعلان <span className="text-[var(--km-pink)]">*</span></p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Choice active={duration === "short"} onClick={() => pickDuration("short")} disabled={useReward} title={DURATION.short} sub={egp(dash.pricing.short)} testId="duration-short" />
          <Choice active={duration === "long"} onClick={() => pickDuration("long")} title={DURATION.long} sub={useReward ? "مجاني" : egp(dash.pricing.long)} testId="duration-long" />
        </div>
      </div>
      <div>
        <p className="font-head mb-2 font-bold text-white">أسلوب الإعلان <span className="text-[var(--km-pink)]">*</span></p>
        {duration === "short" ? (
          <p className="rounded-2xl bg-black/30 p-4 text-sm text-purple-100" data-testid="style-direct-only">الإعلانات من 0 لـ 30 ثانية بتكون بأسلوب <b>مباشر</b> بس.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Choice active={style === "direct"} onClick={() => setStyle("direct")} title={STYLE.direct} sub="نفس السعر" testId="style-direct" />
            <Choice active={style === "story"} onClick={() => setStyle("story")} title={STYLE.story} sub="نفس السعر" testId="style-story" />
          </div>
        )}
      </div>
      <div>
        <p className="font-head mb-2 font-bold text-white">فورمات الفيديو <span className="text-[var(--km-pink)]">*</span></p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Choice active={format === "landscape"} onClick={() => setFormat("landscape")} title={FORMAT.landscape} testId="format-landscape" />
          <Choice active={format === "reels"} onClick={() => setFormat("reels")} title={FORMAT.reels} testId="format-reels" />
        </div>
      </div>
      <label className="block">
        <span className="font-head mb-2 block font-bold text-white">أهم النقاط الإعلانية <span className="text-[var(--km-pink)]">*</span></span>
        <textarea className={`${inputCls} min-h-[140px] resize-y`} value={keyPoints} onChange={(e) => setKeyPoints(e.target.value)} maxLength={4000}
          placeholder="إيه أهم حاجة عايز الناس تعرفها؟ عروض، مميزات، جمهورك، ألوان البراند..." data-testid="key-points-input" />
      </label>
      <label className="block">
        <span className="font-head mb-2 block font-bold text-white">ملاحظات إضافية <span className="text-sm font-normal text-purple-300/60">(اختياري)</span></span>
        <textarea className={`${inputCls} min-h-[100px] resize-y`} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={4000} placeholder="أمثلة عاجباك، أي تفاصيل تانية" data-testid="notes-input" />
      </label>
      <div className="rounded-3xl border-2 border-[var(--km-cyan)]/50 bg-black/40 p-5" data-testid="order-price-summary">
        <div className="flex items-center justify-between"><span className="text-purple-200">السعر النهائي</span><span className="font-display text-4xl text-white" data-testid="order-final-price">{useReward ? "مجاني" : egp(price)}</span></div>
        <div className="mt-2 flex items-center justify-between text-sm"><span className="text-purple-300/70">رصيد محفظتك</span><span className="font-head text-white" data-testid="order-wallet-balance">{egp(c.wallet_balance)}</span></div>
        {!useReward && enough && <div className="mt-1 flex items-center justify-between text-sm"><span className="text-purple-300/70">الرصيد بعد الدفع</span><span className="font-head text-white">{egp(c.wallet_balance - price)}</span></div>}
        {!enough && (
          <div className="mt-4 rounded-2xl bg-red-500/10 p-3 text-sm text-red-200" data-testid="insufficient-balance">
            رصيدك مش كفاية للإعلان ده. <Link to="/wallet" className="font-bold underline" data-testid="go-topup-link">اشحن محفظتك</Link>
          </div>
        )}
      </div>
      {!c.logo && <p className="rounded-2xl bg-yellow-500/10 p-3 text-sm text-yellow-200" data-testid="order-logo-missing">ارفع لوجو البراند من مربع اللوجو فوق.</p>}
      <button type="submit" disabled={busy || !enough || !c.logo} data-testid="create-ad-submit-btn"
        className="font-head inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--km-pink)] px-8 py-4 text-lg font-bold text-white shadow-[0_6px_0_#4B1782] transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 active:translate-y-1 disabled:opacity-50">
        {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> بنأكد طلبك...</> : <><Wallet className="h-5 w-5" /> {useReward ? "أكد الإعلان المجاني" : `أكد وادفع ${egp(price)}`}</>}
      </button>
    </form>
  );
};

export default function CreateAdOrder() {
  const { customer, setCustomer, reload } = useCustomer();
  const [dash, setDash] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (customer) {
      api.get("/customer/dashboard").then((r) => { setDash(r.data); setCustomer(r.data.customer); }).catch(() => {});
    }
  }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="km-grid-bg" data-testid="create-ad-page">
      <Seo title="اعمل إعلانك" description="اطلب إعلان موشن جرافيك من خرابيش موشن: اختار المدة والأسلوب والفورمات وادفع من محفظتك." />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-[1fr_1.4fr] lg:px-8 [&>*]:min-w-0">
        <aside className="relative lg:sticky lg:top-28 lg:self-start">
          <Swoosh className="absolute -top-10 left-0 h-16 w-28" />
          <h1 className="font-display text-5xl leading-tight text-white sm:text-6xl">
            اعمل <span className="relative inline-block text-[var(--km-pink)]">إعلانك<Squiggle className="absolute -bottom-3 right-0 h-5 w-full" color="#E9D8FF" /></span>
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-purple-200/90">اختار مدة الإعلان والأسلوب والفورمات، واكتب أهم النقاط. السعر بيتخصم من محفظتك أول ما تأكد الطلب.</p>
          <ul className="mt-8 space-y-3 text-purple-100">
            {["بياناتك ولوجو البراند بيتملوا لوحدهم من حسابك", "السعر بيتخصم من محفظتك لما تأكد الأوردر", "بتتابع مراحل الإنتاج خطوة بخطوة من حسابك"].map((t, i) => (
              <li key={i} className="flex items-start gap-3"><span className="font-display grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--km-purple)] text-white">{i + 1}</span><span className="pt-1">{t}</span></li>
            ))}
          </ul>
        </aside>
        {customer === null ? <p className="text-purple-300/70">بنحمّل...</p>
          : !customer ? <Gate />
          : done ? <ThankYou order={done} />
          : dash ? <OrderForm dash={dash} onDone={(o) => { setDone(o); reload(); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          : <p className="text-purple-300/70">بنحمّل...</p>}
      </div>
    </div>
  );
}
